import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Trash2, Building2, Search, Package, Wrench,
  IndianRupee, Calculator, Receipt, FileText, Barcode, Gift
} from 'lucide-react';

const CreateBill = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const preselectedService = location.state?.service;

  // Bill type
  const [billType, setBillType] = useState('GST');
  const [taxType, setTaxType] = useState('exclusive'); // 'inclusive' or 'exclusive'
  const [roundOff, setRoundOff] = useState(0);
  const [isIgst, setIsIgst] = useState(false); // Inter-state IGST flag
  const [igstInfo, setIgstInfo] = useState(null); // IGST check result
  
  // Customer
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  
  // Products (Inventory)
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  
  // Pending services for selected customer
  const [pendingServices, setPendingServices] = useState([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  
  // Bill items
  const [items, setItems] = useState([]);
  
  // Bill details
  const [billDate, setBillDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  
  // Loading
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Customer advance adjustment
  const [advanceBalance, setAdvanceBalance] = useState(0);
  const [advanceAdjustment, setAdvanceAdjustment] = useState(0);
  
  // Serial number selection
  const [serialDialogOpen, setSerialDialogOpen] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [availableSerials, setAvailableSerials] = useState([]);
  const [selectedSerials, setSelectedSerials] = useState([]);
  const [serialQuantity, setSerialQuantity] = useState(1);
  const [loadingSerials, setLoadingSerials] = useState(false);

  // Role checks
  const isAdmin = user?.role === 'Admin';
  const isBackOfficeAccounts = user?.role === 'Backoffice Accounts Executive';
  const canAccess = isAdmin || isBackOfficeAccounts;

  // Fetch customers
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await api.get('/customers', { params: { status: 'Active' } });
        setCustomers(response.data);
      } catch (error) {
        console.error('Error fetching customers:', error);
      }
    };
    fetchCustomers();
  }, []);

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.get('/products', { params: { status: 'Active' } });
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };
    fetchProducts();
  }, []);

  // Handle preselected service
  useEffect(() => {
    if (preselectedService && customers.length > 0) {
      const customer = customers.find(c => c.id === preselectedService.customer_id);
      if (customer) {
        setSelectedCustomer(customer);
        setCustomerSearch(customer.company_name);
      }
    }
  }, [preselectedService, customers]);

  // Fetch pending services when customer is selected
  const fetchPendingServices = useCallback(async () => {
    if (!selectedCustomer) {
      setPendingServices([]);
      return;
    }
    
    try {
      const response = await api.get(`/sales-billing/customer/${selectedCustomer.id}/pending-services`);
      setPendingServices(response.data);
      
      // Auto-select preselected service if any
      if (preselectedService) {
        setSelectedServiceIds([preselectedService.id]);
      }
    } catch (error) {
      console.error('Error fetching pending services:', error);
    }
  }, [selectedCustomer, preselectedService]);

  useEffect(() => {
    fetchPendingServices();
  }, [fetchPendingServices]);

  // Fetch customer's available (unutilized) advance balance
  useEffect(() => {
    const fetchAdvanceBalance = async () => {
      if (!selectedCustomer) {
        setAdvanceBalance(0);
        setAdvanceAdjustment(0);
        return;
      }
      try {
        const response = await api.get(`/payment-collections/customer/${selectedCustomer.id}`);
        setAdvanceBalance(response.data?.advance_balance || 0);
      } catch (error) {
        console.error('Error fetching advance balance:', error);
        setAdvanceBalance(0);
      }
      setAdvanceAdjustment(0);
    };
    fetchAdvanceBalance();
  }, [selectedCustomer]);

  // Filter customers based on search
  const filteredCustomers = customers.filter(c => 
    c.company_name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.mobile?.includes(customerSearch) ||
    c.contact_person?.toLowerCase().includes(customerSearch.toLowerCase())
  ).slice(0, 10);

  // Filter products based on search
  const filteredProducts = products.filter(p =>
    p.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.code?.toLowerCase().includes(productSearch.toLowerCase())
  ).slice(0, 10);

  // Check if customer can have GST bill
  const canHaveGSTBill = selectedCustomer?.gst_number && selectedCustomer.gst_number.length >= 15;

  // Check IGST applicability when customer changes
  useEffect(() => {
    const checkIgst = async () => {
      if (selectedCustomer && billType === 'GST' && canHaveGSTBill) {
        try {
          const response = await api.get(`/sales-billing/check-igst/${selectedCustomer.id}`);
          setIsIgst(response.data.is_igst);
          setIgstInfo(response.data);
          if (response.data.is_igst) {
            toast.info('Inter-state supply detected. IGST will be applied instead of CGST+SGST.');
            // Update existing items to use IGST
            setItems(prev => prev.map(item => ({
              ...item,
              igst_percent: (parseFloat(item.cgst_percent) || 9) + (parseFloat(item.sgst_percent) || 9),
              cgst_percent: 0,
              sgst_percent: 0
            })));
          } else {
            // Reset to CGST+SGST
            setItems(prev => prev.map(item => ({
              ...item,
              igst_percent: 0,
              cgst_percent: item.cgst_percent || 9,
              sgst_percent: item.sgst_percent || 9
            })));
          }
        } catch (error) {
          console.error('Error checking IGST:', error);
          setIsIgst(false);
          setIgstInfo(null);
        }
      } else {
        setIsIgst(false);
        setIgstInfo(null);
      }
    };
    checkIgst();
  }, [selectedCustomer, billType]);

  // Handle customer selection
  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.company_name);
    setShowCustomerDropdown(false);
    
    // Check if GST bill is allowed
    if (billType === 'GST' && !customer.gst_number) {
      toast.warning('This customer does not have GST details. Switching to Cash Bill.');
      setBillType('Cash');
    }
  };

  // Handle service selection toggle
  const toggleServiceSelection = (serviceId) => {
    setSelectedServiceIds(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  // Add service items to bill
  const addSelectedServices = () => {
    const selectedServices = pendingServices.filter(s => selectedServiceIds.includes(s.id));
    
    const newItems = selectedServices.map(service => ({
      id: `service-${service.id}`,
      item_type: 'service',
      service_id: service.id,
      description: `Service: ${service.service_type} - ${service.description || service.service_no}`,
      hsn_code: '998719', // SAC code for repair services
      quantity: 1,
      unit_price: service.service_charge || 0,
      discount_percent: 0,
      cgst_percent: billType === 'GST' && !isIgst ? 9 : 0,
      sgst_percent: billType === 'GST' && !isIgst ? 9 : 0,
      igst_percent: billType === 'GST' && isIgst ? 18 : 0,
      is_foc: false
    }));
    
    setItems(prev => [...prev, ...newItems.filter(ni => !prev.some(pi => pi.service_id === ni.service_id))]);
    setSelectedServiceIds([]);
  };

  // Add product item
  const addProduct = async (product) => {
    const existingItem = items.find(i => i.product_id === product.id);
    if (existingItem) {
      toast.error('Product already added');
      return;
    }
    
    // Check if product requires serial numbers
    try {
      setLoadingSerials(true);
      const response = await api.get(`/inventory/product/${product.id}/available-serials`);
      
      if (response.data.requires_serial) {
        // Open serial selection dialog
        setPendingProduct(product);
        setAvailableSerials(response.data.available_serials || []);
        setSelectedSerials([]);
        setSerialQuantity(1);
        setSerialDialogOpen(true);
        setLoadingSerials(false);
        return;
      }
    } catch (error) {
      console.error('Error checking serial requirement:', error);
    } finally {
      setLoadingSerials(false);
    }
    
    // Product doesn't require serial - add directly
    const newItem = {
      id: `product-${product.id}-${Date.now()}`,
      item_type: 'product',
      product_id: product.id,
      description: product.name,
      hsn_code: product.hsn_code || '',
      quantity: 1,
      unit_price: product.selling_price || 0,
      discount_percent: 0,
      cgst_percent: billType === 'GST' && !isIgst ? 9 : 0,
      sgst_percent: billType === 'GST' && !isIgst ? 9 : 0,
      igst_percent: billType === 'GST' && isIgst ? 18 : 0,
      is_foc: false
    };
    
    setItems(prev => [...prev, newItem]);
    setProductSearch('');
  };
  
  // Handle serial number selection toggle
  const toggleSerialSelection = (serialNumber) => {
    setSelectedSerials(prev => 
      prev.includes(serialNumber)
        ? prev.filter(sn => sn !== serialNumber)
        : prev.length < serialQuantity 
          ? [...prev, serialNumber]
          : prev
    );
  };
  
  // Confirm serial selection and add product
  const confirmSerialSelection = () => {
    if (!pendingProduct) return;
    
    if (selectedSerials.length !== serialQuantity) {
      toast.error(`Please select exactly ${serialQuantity} serial number(s)`);
      return;
    }
    
    const newItem = {
      id: `product-${pendingProduct.id}-${Date.now()}`,
      item_type: 'product',
      product_id: pendingProduct.id,
      description: pendingProduct.name,
      hsn_code: pendingProduct.hsn_code || '',
      quantity: serialQuantity,
      unit_price: pendingProduct.selling_price || 0,
      discount_percent: 0,
      cgst_percent: billType === 'GST' && !isIgst ? 9 : 0,
      sgst_percent: billType === 'GST' && !isIgst ? 9 : 0,
      igst_percent: billType === 'GST' && isIgst ? 18 : 0,
      selected_serials: selectedSerials,
      requires_serial: true,
      is_foc: false
    };
    
    setItems(prev => [...prev, newItem]);
    setSerialDialogOpen(false);
    setPendingProduct(null);
    setSelectedSerials([]);
    setAvailableSerials([]);
    setProductSearch('');
    toast.success(`Added ${pendingProduct.name} with ${serialQuantity} serial number(s)`);
  };

  // Add custom item
  const addCustomItem = () => {
    const newItem = {
      id: `custom-${Date.now()}`,
      item_type: 'service',
      description: '',
      hsn_code: '',
      quantity: 1,
      unit_price: 0,
      discount_percent: 0,
      cgst_percent: billType === 'GST' ? 9 : 0,
      sgst_percent: billType === 'GST' ? 9 : 0,
      is_foc: false
    };
    setItems(prev => [...prev, newItem]);
  };

  // Update item
  const updateItem = (id, field, value) => {
    setItems(prev => prev.map(item => {
      if (item.id === id) {
        return { ...item, [field]: value };
      }
      return item;
    }));
  };

  // Remove item
  const removeItem = (id) => {
    setItems(prev => prev.filter(item => item.id !== id));
  };

  // Calculate item totals
  const calculateItemTotal = (item) => {
    // FOC items always have 0 totals
    if (item.is_foc) {
      return { subtotal: 0, discount: 0, taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0 };
    }

    const quantity = parseFloat(item.quantity) || 0;
    const unitPrice = parseFloat(item.unit_price) || 0;
    const discountPercent = parseFloat(item.discount_percent) || 0;
    const cgstPercent = parseFloat(item.cgst_percent) || 0;
    const sgstPercent = parseFloat(item.sgst_percent) || 0;
    const igstPercent = parseFloat(item.igst_percent) || 0;
    
    const grossAmount = quantity * unitPrice;
    const discount = grossAmount * (discountPercent / 100);
    const amountAfterDiscount = grossAmount - discount;
    
    if (isIgst && billType === 'GST') {
      // Inter-state: IGST
      if (taxType === 'inclusive') {
        const taxable = amountAfterDiscount / (1 + igstPercent / 100);
        const igst = taxable * (igstPercent / 100);
        return { subtotal: grossAmount, discount, taxable, cgst: 0, sgst: 0, igst, total: amountAfterDiscount };
      } else {
        const taxable = amountAfterDiscount;
        const igst = taxable * (igstPercent / 100);
        return { subtotal: grossAmount, discount, taxable, cgst: 0, sgst: 0, igst, total: taxable + igst };
      }
    } else {
      // Intra-state: CGST + SGST
      if (taxType === 'inclusive') {
        const totalTaxPercent = cgstPercent + sgstPercent;
        const taxable = amountAfterDiscount / (1 + totalTaxPercent / 100);
        const cgst = taxable * (cgstPercent / 100);
        const sgst = taxable * (sgstPercent / 100);
        return { subtotal: grossAmount, discount, taxable, cgst, sgst, igst: 0, total: amountAfterDiscount };
      } else {
        const taxable = amountAfterDiscount;
        const cgst = taxable * (cgstPercent / 100);
        const sgst = taxable * (sgstPercent / 100);
        return { subtotal: grossAmount, discount, taxable, cgst, sgst, igst: 0, total: taxable + cgst + sgst };
      }
    }
  };

  // Calculate bill totals
  const calculateTotals = () => {
    let subtotal = 0;
    let totalDiscount = 0;
    let totalCgst = 0;
    let totalSgst = 0;
    let totalIgst = 0;
    let totalTaxable = 0;
    
    items.forEach(item => {
      const itemTotals = calculateItemTotal(item);
      subtotal += itemTotals.subtotal;
      totalDiscount += itemTotals.discount;
      totalTaxable += itemTotals.taxable;
      totalCgst += itemTotals.cgst;
      totalSgst += itemTotals.sgst;
      totalIgst += itemTotals.igst;
    });
    
    const totalTax = totalCgst + totalSgst + totalIgst;
    const totalBeforeRoundOff = totalTaxable + totalTax;
    const roundOffValue = parseFloat(roundOff) || 0;
    const netAmount = totalBeforeRoundOff + roundOffValue;
    
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      totalDiscount: Math.round(totalDiscount * 100) / 100,
      taxableAmount: Math.round(totalTaxable * 100) / 100,
      totalCgst: Math.round(totalCgst * 100) / 100,
      totalSgst: Math.round(totalSgst * 100) / 100,
      totalIgst: Math.round(totalIgst * 100) / 100,
      totalTax: Math.round(totalTax * 100) / 100,
      roundOff: roundOffValue,
      netAmount: Math.round(netAmount * 100) / 100
    };
  };

  const totals = calculateTotals();

  // Handle bill type change
  const handleBillTypeChange = (type) => {
    setBillType(type);
    
    // Update GST percentages for all items
    setItems(prev => prev.map(item => ({
      ...item,
      cgst_percent: type === 'GST' && !isIgst ? 9 : 0,
      sgst_percent: type === 'GST' && !isIgst ? 9 : 0,
      igst_percent: type === 'GST' && isIgst ? 18 : 0
    })));
    
    if (type === 'GST' && selectedCustomer && !canHaveGSTBill) {
      toast.error('Selected customer does not have GST details. Cannot create GST Bill.');
      setBillType('Cash');
    }
  };

  // Submit bill
  const handleSubmit = async () => {
    // Validations
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }
    
    if (items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    if (billType === 'GST' && !canHaveGSTBill) {
      toast.error('Cannot create GST Bill for customer without GST details');
      return;
    }
    
    // Check for empty descriptions
    const emptyItem = items.find(i => !i.description || i.description.trim() === '');
    if (emptyItem) {
      toast.error('All items must have a description');
      return;
    }
    
    setSubmitting(true);
    try {
      const billData = {
        bill_type: billType,
        tax_type: taxType,
        is_igst: isIgst,
        customer_id: selectedCustomer.id,
        bill_date: billDate,
        due_date: dueDate || null,
        round_off: parseFloat(roundOff) || 0,
        items: items.map(item => ({
          item_type: item.item_type,
          product_id: item.product_id || null,
          service_id: item.service_id || null,
          description: item.description,
          hsn_code: item.hsn_code || '',
          quantity: parseFloat(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0,
          discount_percent: parseFloat(item.discount_percent) || 0,
          cgst_percent: isIgst ? 0 : (parseFloat(item.cgst_percent) || 0),
          sgst_percent: isIgst ? 0 : (parseFloat(item.sgst_percent) || 0),
          igst_percent: isIgst ? (parseFloat(item.igst_percent) || 0) : 0,
          selected_serials: item.selected_serials || [],
          is_foc: item.is_foc || false
        })),
        notes: notes || '',
        service_ids: items.filter(i => i.service_id).map(i => i.service_id),
        advance_adjustment: parseFloat(advanceAdjustment) || 0
      };
      
      const response = await api.post('/sales-billing/bills', billData);
      const adjusted = response.data?.advance_adjusted || 0;
      toast.success(
        `${billType} Bill created successfully - ${response.data.bill?.bill_no}` +
        (adjusted > 0 ? ` (₹${adjusted.toLocaleString()} advance adjusted)` : '')
      );
      navigate('/sales-billing');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create bill');
    } finally {
      setSubmitting(false);
    }
  };

  if (!canAccess) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="text-red-600">Access Denied</CardTitle>
            <CardDescription>
              Only Admin and Backoffice Accounts Executive can create bills.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="create-bill-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate('/sales-billing')}>
          <ArrowLeft size={18} />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Create {billType} Bill</h1>
          <p className="text-gray-600 mt-1">Generate a new {billType === 'GST' ? 'GST Invoice' : 'Cash Bill'}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Bill Details & Customer */}
        <div className="lg:col-span-2 space-y-6">
          {/* Bill Type Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt size={20} />
                Bill Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <Button
                  variant={billType === 'GST' ? 'default' : 'outline'}
                  className={billType === 'GST' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                  onClick={() => handleBillTypeChange('GST')}
                  disabled={selectedCustomer && !canHaveGSTBill}
                >
                  <FileText size={16} className="mr-2" />
                  GST Bill (INV)
                </Button>
                <Button
                  variant={billType === 'Cash' ? 'default' : 'outline'}
                  className={billType === 'Cash' ? 'bg-green-600 hover:bg-green-700' : ''}
                  onClick={() => handleBillTypeChange('Cash')}
                >
                  <IndianRupee size={16} className="mr-2" />
                  Cash Bill (QCB)
                </Button>
              </div>
              {selectedCustomer && !canHaveGSTBill && (
                <p className="text-sm text-orange-600 mt-2">
                  ⚠️ Customer does not have GST details. Only Cash Bill can be created.
                </p>
              )}
              
              {/* Tax Type Selection */}
              {billType === 'GST' && (
                <div className="mt-4 pt-4 border-t">
                  <Label className="text-sm font-medium mb-2 block">Tax Calculation Type</Label>
                  <div className="flex gap-4">
                    <Button
                      type="button"
                      size="sm"
                      variant={taxType === 'exclusive' ? 'default' : 'outline'}
                      className={taxType === 'exclusive' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                      onClick={() => setTaxType('exclusive')}
                    >
                      Tax Exclusive
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={taxType === 'inclusive' ? 'default' : 'outline'}
                      className={taxType === 'inclusive' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                      onClick={() => setTaxType('inclusive')}
                    >
                      Tax Inclusive
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {taxType === 'inclusive' 
                      ? 'Rate includes GST - tax will be calculated and shown separately'
                      : 'Rate excludes GST - tax will be added on top'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Customer Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 size={20} />
                Customer
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    placeholder="Search customer by name, mobile..."
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      setShowCustomerDropdown(true);
                      if (!e.target.value) setSelectedCustomer(null);
                    }}
                    onFocus={() => setShowCustomerDropdown(true)}
                    className="pl-10"
                    data-testid="customer-search"
                  />
                </div>
                
                {/* Customer Dropdown */}
                {showCustomerDropdown && customerSearch && filteredCustomers.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredCustomers.map(customer => (
                      <div
                        key={customer.id}
                        className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                        onClick={() => handleSelectCustomer(customer)}
                      >
                        <p className="font-medium">{customer.company_name}</p>
                        <p className="text-sm text-gray-500">
                          {customer.mobile} • {customer.city}
                          {customer.gst_number && <span className="ml-2 text-green-600">GST: ✓</span>}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected Customer Details */}
              {selectedCustomer && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Company:</span>
                      <span className="ml-2 font-medium">{selectedCustomer.company_name}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Contact:</span>
                      <span className="ml-2">{selectedCustomer.contact_person}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Mobile:</span>
                      <span className="ml-2">{selectedCustomer.mobile}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">City:</span>
                      <span className="ml-2">{selectedCustomer.city}</span>
                    </div>
                    {selectedCustomer.gst_number && (
                      <div className="col-span-2">
                        <span className="text-gray-500">GSTIN:</span>
                        <span className="ml-2 font-mono text-green-700">{selectedCustomer.gst_number}</span>
                      </div>
                    )}
                    {billType === 'GST' && isIgst && igstInfo && (
                      <div className="col-span-2 mt-2 p-2 bg-orange-50 border border-orange-200 rounded-lg">
                        <p className="text-orange-800 font-medium text-xs">
                          Inter-State Supply — IGST applicable
                        </p>
                        <p className="text-orange-600 text-xs mt-1">
                          Business State: {igstInfo.business_state_code} | Customer State: {igstInfo.customer_state_code}
                        </p>
                      </div>
                    )}
                    {billType === 'GST' && !isIgst && selectedCustomer.gst_number && igstInfo && (
                      <div className="col-span-2 mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-800 font-medium text-xs">
                          Intra-State Supply — CGST + SGST applicable
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pending Services */}
          {selectedCustomer && pendingServices.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wrench size={20} />
                  Pending Services
                  <Badge variant="secondary">{pendingServices.length}</Badge>
                </CardTitle>
                <CardDescription>
                  Select services to include in this bill
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {pendingServices.map(service => (
                    <div 
                      key={service.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedServiceIds.includes(service.id) 
                          ? 'bg-blue-50 border-blue-300' 
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => toggleServiceSelection(service.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Checkbox 
                            checked={selectedServiceIds.includes(service.id)}
                            onChange={() => toggleServiceSelection(service.id)}
                          />
                          <div>
                            <p className="font-medium">{service.service_no}</p>
                            <p className="text-sm text-gray-500">{service.service_type}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-green-700">₹{(service.service_charge || 0).toLocaleString()}</p>
                          <Badge className={service.payment_status === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}>
                            {service.payment_status}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {selectedServiceIds.length > 0 && (
                  <Button 
                    onClick={addSelectedServices} 
                    className="w-full mt-4"
                  >
                    <Plus size={16} className="mr-2" />
                    Add {selectedServiceIds.length} Service(s) to Bill
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Add Products */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package size={20} />
                Add Products (Inventory Sales)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    placeholder="Search product by name or code..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="pl-10"
                    data-testid="product-search"
                  />
                </div>
                
                {/* Products Dropdown */}
                {productSearch && filteredProducts.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredProducts.map(product => (
                      <div
                        key={product.id}
                        className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                        onClick={() => addProduct(product)}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium">{product.name}</p>
                            <p className="text-sm text-gray-500">{product.code} • HSN: {product.hsn_code || '-'}</p>
                          </div>
                          <p className="font-semibold text-green-700">₹{(product.selling_price || 0).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <Button variant="outline" className="mt-4" onClick={addCustomItem}>
                <Plus size={16} className="mr-2" />
                Add Custom Item
              </Button>
            </CardContent>
          </Card>

          {/* Bill Items */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator size={20} />
                Bill Items
              </CardTitle>
            </CardHeader>
            <CardContent>
              {items.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Receipt className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No items added yet</p>
                  <p className="text-sm mt-1">Add services or products from above</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[300px]">Description</TableHead>
                        <TableHead>HSN</TableHead>
                        <TableHead className="w-16">FOC</TableHead>
                        <TableHead className="w-20">Qty</TableHead>
                        <TableHead className="w-28">Rate (₹)</TableHead>
                        <TableHead className="w-20">Disc %</TableHead>
                        {billType === 'GST' && (
                          <>
                            {isIgst ? (
                              <TableHead className="w-20">IGST %</TableHead>
                            ) : (
                              <>
                                <TableHead className="w-20">CGST %</TableHead>
                                <TableHead className="w-20">SGST %</TableHead>
                              </>
                            )}
                          </>
                        )}
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((item, index) => {
                        const itemTotals = calculateItemTotal(item);
                        return (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Input
                                value={item.description}
                                onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                                placeholder="Item description"
                                className="min-w-[200px]"
                              />
                              <div className="flex flex-wrap gap-1 mt-1">
                                {item.is_foc && (
                                  <Badge className="bg-amber-100 text-amber-800 text-xs"><Gift className="h-3 w-3 mr-1" />FOC</Badge>
                                )}
                                {item.item_type === 'service' && item.service_id && (
                                  <Badge variant="outline" className="text-xs">Service</Badge>
                                )}
                                {item.item_type === 'product' && item.product_id && (
                                  <Badge variant="outline" className="text-xs">Product</Badge>
                                )}
                              </div>
                              {item.selected_serials && item.selected_serials.length > 0 && (
                                <div className="mt-1">
                                  <div className="flex flex-wrap gap-1">
                                    {item.selected_serials.map(sn => (
                                      <Badge key={sn} variant="secondary" className="text-xs font-mono">
                                        <Barcode className="h-3 w-3 mr-1" />{sn}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              <Input
                                value={item.hsn_code}
                                onChange={(e) => updateItem(item.id, 'hsn_code', e.target.value)}
                                placeholder="HSN"
                                className="w-20"
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-center">
                                <Checkbox
                                  checked={item.is_foc || false}
                                  onCheckedChange={(checked) => updateItem(item.id, 'is_foc', checked)}
                                  data-testid={`foc-checkbox-${item.id}`}
                                />
                              </div>
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => updateItem(item.id, 'quantity', e.target.value)}
                                min="1"
                                className="w-16"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={item.unit_price}
                                onChange={(e) => updateItem(item.id, 'unit_price', e.target.value)}
                                min="0"
                                className="w-24"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={item.discount_percent}
                                onChange={(e) => updateItem(item.id, 'discount_percent', e.target.value)}
                                min="0"
                                max="100"
                                className="w-16"
                              />
                            </TableCell>
                            {billType === 'GST' && (
                              <>
                                {isIgst ? (
                                  <TableCell>
                                    <Input
                                      type="number"
                                      value={item.igst_percent}
                                      onChange={(e) => updateItem(item.id, 'igst_percent', e.target.value)}
                                      min="0"
                                      className="w-16"
                                    />
                                  </TableCell>
                                ) : (
                                  <>
                                    <TableCell>
                                      <Input
                                        type="number"
                                        value={item.cgst_percent}
                                        onChange={(e) => updateItem(item.id, 'cgst_percent', e.target.value)}
                                        min="0"
                                        className="w-16"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Input
                                        type="number"
                                        value={item.sgst_percent}
                                        onChange={(e) => updateItem(item.id, 'sgst_percent', e.target.value)}
                                        min="0"
                                        className="w-16"
                                      />
                                    </TableCell>
                                  </>
                                )}
                              </>
                            )}
                            <TableCell className="text-right font-semibold">
                              ₹{itemTotals.total.toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeItem(item.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Summary & Actions */}
        <div className="space-y-6">
          {/* Bill Details */}
          <Card>
            <CardHeader>
              <CardTitle>Bill Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Bill Date *</Label>
                <Input
                  type="date"
                  value={billDate}
                  onChange={(e) => setBillDate(e.target.value)}
                  data-testid="bill-date"
                />
              </div>
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  min={billDate}
                  data-testid="due-date"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional notes..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Bill Summary */}
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardHeader>
              <CardTitle className="text-blue-800">Bill Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal:</span>
                  <span>₹{totals.subtotal.toLocaleString()}</span>
                </div>
                
                {totals.totalDiscount > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>Discount:</span>
                    <span>-₹{totals.totalDiscount.toLocaleString()}</span>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Taxable Amount:</span>
                  <span>₹{totals.taxableAmount.toLocaleString()}</span>
                </div>
                
                {billType === 'GST' && (
                  <>
                    {isIgst ? (
                      <div className="flex justify-between text-gray-600">
                        <span>IGST:</span>
                        <span>₹{totals.totalIgst.toLocaleString()}</span>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between text-gray-600">
                          <span>CGST:</span>
                          <span>₹{totals.totalCgst.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                          <span>SGST:</span>
                          <span>₹{totals.totalSgst.toLocaleString()}</span>
                        </div>
                      </>
                    )}
                  </>
                )}
                
                {/* Round Off */}
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Round Off:</span>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={roundOff}
                      onChange={(e) => setRoundOff(e.target.value)}
                      step="0.01"
                      className="w-24 text-right h-8"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-400">Enter +ve to add, -ve to subtract</p>
                
                <div className="border-t pt-3 flex justify-between text-lg font-bold text-blue-800">
                  <span>Net Amount:</span>
                  <span>₹{totals.netAmount.toLocaleString()}</span>
                </div>

                {/* Advance Adjustment (optional) */}
                {advanceBalance > 0 && (
                  <div className="border-t pt-3 space-y-2" data-testid="advance-adjustment-section">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-green-700 font-medium">Available Advance:</span>
                      <span className="font-semibold text-green-700" data-testid="advance-available">₹{advanceBalance.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 text-sm">Adjust Advance (optional):</span>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={advanceAdjustment}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            const maxAdj = Math.min(advanceBalance, totals.netAmount);
                            setAdvanceAdjustment(val < 0 ? 0 : val > maxAdj ? maxAdj : val);
                          }}
                          step="0.01"
                          className="w-28 text-right h-8"
                          placeholder="0.00"
                          data-testid="advance-adjustment-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs"
                          onClick={() => setAdvanceAdjustment(Math.min(advanceBalance, totals.netAmount))}
                          data-testid="advance-adjust-max"
                        >
                          Max
                        </Button>
                      </div>
                    </div>
                    {advanceAdjustment > 0 && (
                      <div className="flex justify-between items-center text-base font-bold text-orange-700">
                        <span>Balance Due:</span>
                        <span data-testid="balance-due">₹{Math.max(0, totals.netAmount - advanceAdjustment).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="space-y-3">
            <Button
              className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-lg"
              onClick={handleSubmit}
              disabled={submitting || items.length === 0 || !selectedCustomer}
              data-testid="submit-bill"
            >
              {submitting ? 'Creating...' : `Create ${billType} Bill`}
            </Button>
            
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate('/sales-billing')}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
      
      {/* Serial Number Selection Dialog */}
      <Dialog open={serialDialogOpen} onOpenChange={setSerialDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Barcode className="h-5 w-5" />
              Select Serial Numbers - {pendingProduct?.name}
            </DialogTitle>
            <DialogDescription>
              This product requires serial number selection. Select {serialQuantity} serial number(s) from available inventory.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 flex-1 overflow-auto">
            {/* Quantity selector */}
            <div className="flex items-center gap-4">
              <Label>Quantity:</Label>
              <Input
                type="number"
                min="1"
                max={availableSerials.length}
                value={serialQuantity}
                onChange={(e) => {
                  const newQty = parseInt(e.target.value) || 1;
                  setSerialQuantity(Math.min(newQty, availableSerials.length));
                  // Clear selections if quantity changes
                  setSelectedSerials(prev => prev.slice(0, newQty));
                }}
                className="w-24"
              />
              <Badge variant="outline">
                {availableSerials.length} available
              </Badge>
            </div>
            
            {/* Serial number list */}
            {availableSerials.length > 0 ? (
              <div className="border rounded-lg overflow-auto max-h-[300px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Select</TableHead>
                      <TableHead>Serial Number</TableHead>
                      <TableHead>Purchase Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {availableSerials.map((serial, idx) => (
                      <TableRow 
                        key={serial.serial_number || idx}
                        className={selectedSerials.includes(serial.serial_number) ? 'bg-blue-50' : ''}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedSerials.includes(serial.serial_number)}
                            onCheckedChange={() => toggleSerialSelection(serial.serial_number)}
                            disabled={!selectedSerials.includes(serial.serial_number) && selectedSerials.length >= serialQuantity}
                            data-testid={`serial-checkbox-${idx}`}
                          />
                        </TableCell>
                        <TableCell className="font-mono">{serial.serial_number}</TableCell>
                        <TableCell>{serial.purchase_date ? new Date(serial.purchase_date).toLocaleDateString() : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No serial numbers available for this product</p>
                <p className="text-sm">Please add stock with serial numbers first</p>
              </div>
            )}
            
            {/* Selected summary */}
            {selectedSerials.length > 0 && (
              <div className="bg-green-50 p-3 rounded-lg">
                <Label className="text-green-800">Selected Serial Numbers ({selectedSerials.length}/{serialQuantity}):</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedSerials.map(sn => (
                    <Badge key={sn} variant="default" className="bg-green-600">
                      {sn}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setSerialDialogOpen(false);
              setPendingProduct(null);
              setSelectedSerials([]);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={confirmSerialSelection}
              disabled={selectedSerials.length !== serialQuantity || availableSerials.length === 0}
              data-testid="confirm-serials-btn"
            >
              Add to Bill ({selectedSerials.length}/{serialQuantity} selected)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreateBill;
